import { Connection, DATA_TYPE, ITable } from 'jsstore';
import { initLocalDBConnection } from './idb_helper';

export interface IUploadRecord {
	id?: number; // auto-increment
	batchId?: string;
	bucket: string;
	key: string;
	file?: File | null;
	uploadId?: string;
	parts: { ETag: string; PartNumber: number }[];
	status: 'pending' | 'uploading' | 'completed' | 'failed';
	createdAt: number;
	updatedAt?: number;
	lastModified?: number;
}

const TBL_UPLOADS: ITable = {
	name: 'Uploads',
	columns: {
		id: { primaryKey: true, autoIncrement: true },
		batchId: { dataType: DATA_TYPE.String },
		bucket: { dataType: DATA_TYPE.String },
		key: { dataType: DATA_TYPE.String },
		file: { dataType: DATA_TYPE.Object },
		uploadId: { dataType: DATA_TYPE.String },
		parts: { dataType: DATA_TYPE.Array },
		status: { dataType: DATA_TYPE.String },
		createdAt: { dataType: DATA_TYPE.Number },
		updatedAt: { dataType: DATA_TYPE.Number },
		lastModified: { dataType: DATA_TYPE.Number }
	}
};

let connection: Connection | null = null;

export const getUploadsDB = async () => {
	if (connection) return connection;
	connection = await initLocalDBConnection();
	await connection.initDb({
		name: 'CloudFileSystemUploadsDB',
		tables: [TBL_UPLOADS]
	});
	return connection;
};

export const addUploadRecord = async (record: Omit<IUploadRecord, 'id'>) => {
	const db = await getUploadsDB();
	const result = await db.insert<IUploadRecord>({
		into: 'Uploads',
		values: [record],
		return: true
	});
	return (result as IUploadRecord[])[0];
};

export const addUploadRecords = async (
	records: Omit<IUploadRecord, 'id'>[]
) => {
	if (records.length === 0) return [];
	const db = await getUploadsDB();
	const result = await db.insert<IUploadRecord>({
		into: 'Uploads',
		values: records,
		return: true
	});
	return result as IUploadRecord[];
};

export const updateUploadRecord = async (
	id: number,
	updates: Partial<IUploadRecord>
) => {
	const db = await getUploadsDB();
	await db.update({
		in: 'Uploads',
		set: {
			...updates,
			updatedAt: Date.now()
		},
		where: {
			id: id
		}
	});
};

export const removeUploadRecord = async (id: number) => {
	const db = await getUploadsDB();
	await db.remove({
		from: 'Uploads',
		where: {
			id: id
		}
	});
};

export const deleteBatchUploads = async (batchId: string) => {
	const db = await getUploadsDB();
	await db.remove({
		from: 'Uploads',
		where: {
			batchId: batchId
		}
	});
};

export const getAllBucketUploads = async (bucket?: string) => {
	const db = await getUploadsDB();
	if (bucket) {
		return await db.select<IUploadRecord>({
			from: 'Uploads',
			where: {
				bucket: bucket
			}
		});
	}
	return await db.select<IUploadRecord>({
		from: 'Uploads'
	});
};

export const getPendingUploads = async (bucket?: string) => {
	const db = await getUploadsDB();
	const results = bucket
		? await db.select<IUploadRecord>({
				from: 'Uploads',
				where: {
					bucket: bucket
				}
			})
		: await db.select<IUploadRecord>({
				from: 'Uploads'
			});
	return results.filter((r) => r.status !== 'completed');
};
